import { from, Observable } from "rxjs";
import { PageRequest } from "src/app/support/page-request";
import { Paginated } from "src/app/support/paginated";
import { rxRandomErrorDelayAsync } from "src/app/support/rx-random-error-delay";
import { map, switchMap } from "rxjs/operators";
import { find } from "lodash-es";
import { marker } from "@jsverse/transloco-keys-manager/marker";
import { UserNotify } from "src/app/support/notify-decorator";
import { UUID, uuid } from "src/app/support/uuid";
import {
  loadDemoStorage,
  writeDemoStorage
} from "src/app/support/demo-storage";
import { Injectable } from "@angular/core";
import { Resource } from "../models/resource/resource";
import { ResourceCreate } from "../models/resource/resource.create";
import { ResourceDelete } from "../models/resource/resource.delete";
import { ResourceLoad } from "../models/resource/resource.load";
import { ResourceUpdate } from "../models/resource/resource.update";

const DB_NAME = "resource";

@Injectable()
export class ResourceBackend {
  load(request: ResourceLoad): Observable<Resource> {
    return rxRandomErrorDelayAsync(loadDb()).pipe(
      map((list) => {
        return find(list, (x) => x.id === request.uuid)!;
      })
    );
  }

  list(request: PageRequest): Observable<Paginated<Resource>> {
    return rxRandomErrorDelayAsync(loadDb()).pipe(
      map((list) => {
        return <Paginated<Resource>>{
          list,
          count: list.length
        };
      })
    );
  }

  @UserNotify({
    successMessage: marker("Risorsa creata"),
    errorMessage: marker("Impossibile creare la risorsa")
  })
  create(request: ResourceCreate): Observable<UUID> {
    return rxRandomErrorDelayAsync(loadDb()).pipe(
      switchMap((list) => {
        const newUuid = uuid();
        const newResource: Partial<Resource> = {
          id: newUuid,
          name: request.name,
          color: request.color
        };

        return from(writeDemoStorage(DB_NAME, [...list, newResource])).pipe(
          map(() => newUuid)
        );
      })
    );
  }

  @UserNotify({
    successMessage: marker("Risorsa aggiornata"),
    errorMessage: marker("Impossibile aggiornare la risorsa")
  })
  update(request: ResourceUpdate): Observable<void> {
    return rxRandomErrorDelayAsync(loadDb()).pipe(
      switchMap((list) => {
        return from(
          writeDemoStorage(
            DB_NAME,
            list.map((x) => {
              if (x.id === request.id) {
                return {
                  ...x,
                  ...request
                };
              }

              return x;
            })
          )
        );
      })
    );
  }

  @UserNotify({
    successMessage: marker("Risorsa rimossa"),
    errorMessage: marker("Impossibile rimuovere la risorsa")
  })
  delete(request: ResourceDelete): Observable<void> {
    return rxRandomErrorDelayAsync(loadDb()).pipe(
      switchMap((list) => {
        return from(
          writeDemoStorage(
            DB_NAME,
            list.filter((x) => {
              return x.id !== request.id;
            })
          )
        );
      })
    );
  }
}

async function loadDb(): Promise<Resource[]> {
  return (await loadDemoStorage<Resource[]>(DB_NAME)) || [];
}
